import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { MOBILE_PHONE_WIDTH_THRESHOLD, MOBILE_WIDTH_THRESHOLD } from '../social-mini-map/mini-map-helper.constants';

export interface BrowserCheck {
  isAvailable: boolean;
  showWarning: boolean;
  earlyAccess: boolean;
  isIOS: boolean;
}

export enum BrowserType {
  CHROME = 'CHROME',
  FIREFOX = 'FIREFOX',
  SAFARI = 'SAFARI',
  EDGE = 'EDGE',
  UNSUPPORTED = 'UNSUPPORTED',
}

const setChecks = ({
  earlyAccess,
  isAvailable,
  isIOS,
  showWarning,
}: {
  isAvailable: boolean;
  showWarning?: boolean;
  earlyAccess?: boolean;
  isIOS?: boolean;
}): BrowserCheck => ({ isAvailable, showWarning: !!showWarning, earlyAccess: !!earlyAccess, isIOS: !!isIOS });

const LINUX_ChromeV = 87;
const LINUX_EdgeV = 87;
const LINUX_FirefoxV = 84;
const MAC_ChromeV = 87;
const MAC_EdgeV = 87;
const MAC_FirefoxV = 84;
const MAC_SafariV = 14;
const WINDOWS_ChromeV = 87;
const WINDOWS_EdgeV = 87;
const WINDOWS_FirefoxV = 84;
const ANDROID_ChromeV = 87;

/**
 * BrowserService
 *
 * This service manages data about the current user agent.
 * This includes which user agents are allowed, and attributes about host the user agent is running on (desktop, tablet, phone)
 */
@Injectable({
  providedIn: 'root',
})
export class BrowserService extends Loggable implements OnDestroy {
  private deviceDetectorService = inject(DeviceDetectorService);
  private store = inject<Store<AppState>>(Store);

  public isSafari = false;
  public isFirefox = false;

  private enableSafari = false;
  private enableFirefox = false;
  private isBrowserCheckEnabled = true;
  private isDeviceCheckEnabled = false;
  private testUser = false;
  private mobileBreakpoint = MOBILE_PHONE_WIDTH_THRESHOLD;
  private tabletBreakpoint = MOBILE_WIDTH_THRESHOLD;
  private _browserType = BrowserType.UNSUPPORTED;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('BrowserService');

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ isTestUser }) => (this.testUser = isTestUser));
  }

  public checkBrowserAvailability(): Promise<BrowserCheck> {
    const safariFlag$: Observable<boolean> = getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_safari);
    const firefoxFlag$ = getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_firefox);
    const browserCheckFlag$ = getFeatureFlagObservable(this.store, LaunchDarklyFlags.browser_check);
    const deviceCheckFlag$ = getFeatureFlagObservable(this.store, LaunchDarklyFlags.device_check);
    return new Promise((resolve) => {
      combineLatest([safariFlag$, firefoxFlag$, browserCheckFlag$, deviceCheckFlag$])
        .pipe(takeUntil(this.destroyed$))
        .pipe(
          catchError((error) => {
            return of(error);
          }),
        )
        .subscribe(([enablesSafari, enablesFirefox, isBrowserCheckEnabled, isDeviceCheckEnabled]) => {
          this.enableSafari = enablesSafari;
          this.enableFirefox = enablesFirefox;
          this.isBrowserCheckEnabled = isBrowserCheckEnabled;
          this.isDeviceCheckEnabled = isDeviceCheckEnabled;

          const service = this.deviceDetectorService;
          const device = service.device;
          const browser = service.browser;
          const os = service.os;
          const version = service.browser_version.split('.')[0];
          const version_number = Number(version);

          const isChrome = browser === 'Chrome';
          const isSafari = browser === 'Safari';
          const isEdge = browser === 'MS-Edge-Chromium';
          const isFirefox = browser === 'Firefox';

          this.isSafari = isSafari;
          this.isFirefox = isFirefox;

          if (isFirefox) {
            this._browserType = BrowserType.FIREFOX;
          } else if (isChrome) {
            this._browserType = BrowserType.CHROME;
          } else if (isEdge) {
            this._browserType = BrowserType.EDGE;
          } else if (isSafari) {
            this._browserType = BrowserType.SAFARI;
          }

          this.info(
            `Device & Browser check - Running on ${device} with ${os} and browser ${browser} with object: `,
            service,
          );

          if (!this.isBrowserCheckEnabled) {
            resolve(setChecks({ isAvailable: true }));
          }

          if (this.testUser) {
            resolve(setChecks({ isAvailable: true }));
          }

          // See: https://developer.mozilla.org/en-US/docs/Web/API/Navigator
          // @ts-expect-error iPadOS lies about its OS. navigator.standalone is a boolean only set on iOS devices
          const hasStandalone = typeof navigator.standalone === 'boolean' ? navigator.standalone : false;
          if (hasStandalone && isSafari) {
            resolve(setChecks({ isAvailable: false, isIOS: true }));
          }

          if (os === 'iOS' || device === 'iPad' || device === 'iPhone') {
            resolve(setChecks({ isAvailable: false, isIOS: true }));
          }

          if (os === 'Mac') {
            if (
              (isChrome && version_number < MAC_ChromeV) ||
              (isEdge && version_number < MAC_EdgeV) ||
              (isFirefox && version_number < MAC_FirefoxV) ||
              (isSafari && version_number < MAC_SafariV)
            ) {
              resolve(setChecks({ isAvailable: false }));
            }

            if (isFirefox) {
              resolve(setChecks({ isAvailable: this.enableFirefox, earlyAccess: true }));
            }

            if (isChrome || isEdge) {
              resolve(setChecks({ isAvailable: true }));
            }

            if (isSafari) {
              resolve(setChecks({ isAvailable: this.enableSafari }));
            }
            resolve(setChecks({ isAvailable: false }));
          }

          if (os === 'Windows') {
            if (
              (isChrome && version_number < WINDOWS_ChromeV) ||
              (isEdge && version_number < WINDOWS_EdgeV) ||
              (isFirefox && version_number < WINDOWS_FirefoxV)
            ) {
              resolve(setChecks({ isAvailable: false }));
            }

            if (isFirefox) {
              resolve(setChecks({ isAvailable: this.enableFirefox, earlyAccess: true }));
            }

            if (isChrome || isEdge) {
              resolve(setChecks({ isAvailable: true }));
            }

            resolve(setChecks({ isAvailable: false }));
          }

          if (os === 'Linux') {
            if (
              (isChrome && version_number < LINUX_ChromeV) ||
              (isEdge && version_number < LINUX_EdgeV) ||
              (isFirefox && version_number < LINUX_FirefoxV)
            ) {
              resolve(setChecks({ isAvailable: false }));
            }

            if (isFirefox) {
              resolve(setChecks({ isAvailable: this.enableFirefox, earlyAccess: true }));
            }

            if (isChrome || isEdge) {
              resolve(setChecks({ isAvailable: true, earlyAccess: true }));
            }

            resolve(setChecks({ isAvailable: false }));
          }

          if (os === 'Chrome-OS') {
            if (isChrome && version_number < LINUX_ChromeV) {
              resolve(setChecks({ isAvailable: false }));
            }

            if (isChrome) {
              resolve(setChecks({ isAvailable: true, earlyAccess: true }));
            }

            resolve(setChecks({ isAvailable: false }));
          }

          if (os === 'Android') {
            if (isFirefox) {
              resolve(setChecks({ isAvailable: this.enableFirefox, earlyAccess: true }));
            }

            if (isChrome && version_number < ANDROID_ChromeV) {
              resolve(setChecks({ isAvailable: false }));
            }

            if (isChrome || isEdge) {
              resolve(setChecks({ isAvailable: true, earlyAccess: true }));
            }

            resolve(setChecks({ isAvailable: false }));
          }

          resolve(setChecks({ isAvailable: false }));
        });
    });
  }

  public checkIsMobileOrTablet(): boolean {
    if (this.isDeviceCheckEnabled) {
      return false;
    }
    const isMobile = this.deviceDetectorService.isMobile();
    const isTablet = this.deviceDetectorService.isTablet();

    if (isMobile || isTablet) {
      return true;
    }
    return false;
  }

  // Getter for isMobileOrTablet
  public get isMobileOrTablet(): boolean {
    return this.checkIsMobileOrTablet();
  }

  public isMobile(): boolean {
    return !this.isDeviceCheckEnabled && this.deviceDetectorService.isMobile();
  }

  public isAndroidDevice(): boolean {
    return this.deviceDetectorService.os.includes('Android');
  }

  public isMobileBreakpoint(): boolean {
    return window.innerWidth < this.mobileBreakpoint;
  }

  public isTabletOrMobileBreakpoint(): boolean {
    return window.innerWidth < this.tabletBreakpoint;
  }

  public get browserType(): BrowserType {
    return this._browserType;
  }

  public destroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
